/**
 * Meddelande om hyreshöjning enligt 12 kap. 54 a § jordabalken.
 *
 * The landlord's one-way letter under the passivitetsregel: no numbered
 * points and no signatures — it is exported and sent by registered post.
 * Every party/property datum is a slot value with an autofill binding.
 *
 * Legal text verbatim from `base-templates/Mall - Villkorsändringsmeddelande
 * 12 kap 54 a § JB .docx` (Haymanot-approved). DO NOT paraphrase or reorder.
 */
import { nodes, p, t, val } from '@hyranai/regta-document-ir';
import { dateVal, fieldRows, formatSlotNumber, numVal, partySlot, propertySlot, slotNumber, textVal } from '../common/index.js';
import { boxedRows, letterOpening } from '../andring_hyresvillkor/kit.js';
const LEGAL = {
    plane: 'legal',
    status: 'published',
    language: 'sv-SE',
    legalReview: { reviewedBy: 'Haymanot', source: 'base-templates/Mall - Villkorsändringsmeddelande 12 kap 54 a § JB .docx' },
};
export const rubrik = {
    ...LEGAL,
    blockKey: 'villkorsandring_54a.rubrik',
    version: 'v1',
    title: 'Rubrik',
    summary: 'Opening of the letter: object type, title, statute and the registered-post instruction.',
    slots: [],
    compile: () => letterOpening({
        kicker: 'Bostadslägenhet',
        title: 'Meddelande om hyreshöjning',
        subtitle: 'enligt 12 kap. 54 a § jordabalken',
        note: 'Att skickas med rekommenderad post',
    }),
};
/** [Standardfält om hyresavtalet] — hyresvärd, hyresgäst, lägenhet. */
export const uppgifter = {
    ...LEGAL,
    blockKey: 'villkorsandring_54a.uppgifter',
    version: 'v1',
    title: 'Uppgifter om hyresavtalet',
    summary: 'Boxed table: landlord, tenant and the apartment.',
    slots: [
        partySlot('landlord_name', 'Namn', 'lessor', 0, 'name', { required: true }),
        partySlot('landlord_org_number', 'Personnr / Org.nr', 'lessor', 0, 'legalId'),
        partySlot('landlord_address', 'Adress', 'lessor', 0, 'address'),
        partySlot('tenant_name', 'Namn', 'lessee', 0, 'name', { required: true }),
        partySlot('tenant_personal_number', 'Personnummer', 'lessee', 0, 'legalId'),
        partySlot('tenant_address', 'Adress', 'lessee', 0, 'address'),
        propertySlot('property_designation', 'Fastighetsbeteckning', 'designation', { required: true }),
        propertySlot('property_municipality', 'Kommun', 'municipality'),
        propertySlot('property_address', 'Postadress', 'address', { type: 'textarea', required: true }),
        { key: 'apartment_official_number', label: 'Lägenhetsnummer (officiell)', type: 'text' },
    ],
    compile: (input) => [
        boxedRows(input, [
            ['Hyresvärd', 'landlord_name'],
            ['Personnr / org.nr', 'landlord_org_number'],
            ['Hyresvärdens adress', 'landlord_address'],
            ['Hyresgäst', 'tenant_name'],
            ['Personnummer', 'tenant_personal_number'],
            ['Hyresgästens adress', 'tenant_address'],
            ['Fastighetsbeteckning', 'property_designation'],
            ['Kommun', 'property_municipality'],
            ['Lägenhetens adress', 'property_address'],
            ['Lägenhetsnummer (officiell)', 'apartment_official_number'],
        ]),
    ],
};
export const hyreshojning = {
    ...LEGAL,
    blockKey: 'villkorsandring_54a.hyreshojning',
    version: 'v1',
    title: 'Hyreshöjning',
    summary: 'The requested rent increase (current, requested, computed difference), its start date, the objection deadline and the hyresnämnd route.',
    slots: [
        { key: 'current_rent', label: 'Nuvarande hyra (kr/månad)', type: 'currency', required: true },
        {
            key: 'requested_rent',
            label: 'Begärd hyra (kr/månad)',
            type: 'currency',
            required: true,
            help: 'Höjningen (skillnaden mot nuvarande hyra) räknas ut automatiskt i meddelandet.',
        },
        { key: 'new_rent_date', label: 'Ny hyra gäller fr.o.m.', type: 'date', required: true },
        {
            key: 'objection_deadline',
            label: 'Sista dag att motsätta sig höjningen',
            type: 'date',
            required: true,
            help: 'Måste vara minst två månader efter den dag då meddelandet avsänds. Hyresgästen blir bunden av den nya hyran om hen inte skriftligen motsätter sig den senast detta datum.',
        },
    ],
    compile: (input, ctx) => {
        const current = slotNumber(input.current_rent);
        const requested = slotNumber(input.requested_rent);
        const increase = current != null && requested != null ? requested - current : null;
        return [
            p([
                t('Hyresvärden begär att hyran för lägenheten höjs från '),
                numVal(ctx, input, 'current_rent', { blankWidth: 'medium' }),
                t(' kronor per månad till '),
                numVal(ctx, input, 'requested_rent', { blankWidth: 'medium' }),
                t(' kronor per månad, vilket innebär en höjning om '),
                val(formatSlotNumber(ctx, increase), { format: 'number', raw: increase, blankWidth: 'medium' }),
                t(' kronor per månad.'),
            ]),
            p([t('Den nya hyran begärs gälla från och med den '), dateVal(ctx, input, 'new_rent_date'), t('.')]),
            p([
                t('Om hyresgästen inte senast den '),
                dateVal(ctx, input, 'objection_deadline'),
                t(' skriftligen motsätter sig den begärda hyreshöjningen blir hyresgästen bunden av den nya hyran från och med den dag som anges ovan.'),
            ]),
            p('Om hyresgästen inom angiven tid motsätter sig den begärda hyreshöjningen och parterna därefter inte kan komma överens, kan hyresvärden ansöka hos hyresnämnden om ändring av hyresvillkoren. Hyresnämnden prövar då skäligheten av den begärda hyran.'),
        ];
    },
};
export const kontakt = {
    ...LEGAL,
    blockKey: 'villkorsandring_54a.kontakt',
    version: 'v1',
    title: 'Kontaktuppgifter',
    summary: 'How the tenant can contact the landlord about the increase.',
    slots: [
        partySlot('contact_phone', 'Telefonnummer', 'lessor', 0, 'phone'),
        partySlot('contact_email', 'E-postadress', 'lessor', 0, 'email'),
    ],
    compile: (input) => nodes(p('Vid frågor om den begärda hyreshöjningen kan hyresvärden kontaktas på följande sätt:'), fieldRows([
        ['Telefonnummer', [textVal(input, 'contact_phone')]],
        ['E-postadress', [textVal(input, 'contact_email')]],
        ['Postadress', [textVal(input, 'landlord_address')]],
    ])),
};
export const VILLKORSANDRING_54A_BLOCKS = [rubrik, uppgifter, hyreshojning, kontakt];
//# sourceMappingURL=blocks.js.map